<script setup lang="ts">
import { ref } from 'vue'
import { RouterLink } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { Button } from '@/components/ui/button'
import { availableLanguages, setLocale, getLocale, type SupportedLocale } from '@/i18n'

const authStore = useAuthStore()
const langOpen = ref(false)
const currentLang = ref(getLocale())

const currentFlag = () => availableLanguages.find(l => l.code === currentLang.value)?.flag || '🇬🇧'

function switchLang(code: string) {
  setLocale(code as SupportedLocale)
  currentLang.value = code as SupportedLocale
  langOpen.value = false
}
</script>

<template>
  <nav class="border-b bg-background">
    <div class="container flex h-16 items-center px-4">
      <!-- Logo -->
      <RouterLink to="/" class="flex items-center space-x-2">
        <img src="/vte-logo-blue-500.jpg" alt="Virtual Tours Easy — Create your 360° virtual tours in 5min" title="Virtual Tours Easy" class="h-10 w-auto" />
      </RouterLink>

      <!-- Spacer -->
      <div class="flex-1"></div>

      <!-- Language Selector -->
      <div class="relative mr-2">
        <button
          @click="langOpen = !langOpen"
          class="flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-muted transition-colors text-sm"
        >
          <span class="text-base">{{ currentFlag() }}</span>
          <svg class="w-3.5 h-3.5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
        </button>
        <div
          v-if="langOpen"
          class="absolute right-0 top-full mt-1 bg-popover border rounded-md shadow-lg py-1 z-50 min-w-[140px]"
        >
          <button
            v-for="lang in availableLanguages"
            :key="lang.code"
            @click="switchLang(lang.code)"
            :class="[
              'w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted transition-colors text-left',
              lang.code === currentLang ? 'font-medium text-foreground' : 'text-muted-foreground'
            ]"
          >
            <span>{{ lang.flag }}</span>
            <span>{{ lang.nativeName }}</span>
          </button>
        </div>
      </div>

      <!-- Auth Buttons -->
      <div class="flex items-center space-x-4">
        <template v-if="authStore.isAuthenticated">
          <RouterLink to="/dashboard">
            <Button size="sm">
              Go to Dashboard
            </Button>
          </RouterLink>
        </template>
        <template v-else>
          <RouterLink to="/login">
            <Button variant="ghost" size="sm">
              Login
            </Button>
          </RouterLink>
          <RouterLink to="/register">
            <Button size="sm">
              Get Started
            </Button>
          </RouterLink>
        </template>
      </div>
    </div>
  </nav>
</template>
